import React, {useState} from "react";
import {ChartBarIcon, CheckCircleIcon, SparklesIcon, UsersIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {Bars3Icon} from "@heroicons/react/16/solid";
import {Link} from "react-router-dom";

function LandingPage() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    return (
        <div className="min-h-screen bg-white w-full">
            {/* --- Header Section --- */}
            <header className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6">
                <nav className="container mx-auto flex justify-between items-center px-4 sm:px-6 lg:px-8">
                    <div className="text-2xl font-bold">
                        LocalRewards
                    </div>

                    {/* Mobile menu button */}
                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="md:hidden p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                    >
                        {isMenuOpen ? (
                            <XMarkIcon className="h-6 w-6" />
                        ) : (
                            <Bars3Icon className="h-6 w-6" />
                        )}
                    </button>

                    {/* Desktop navigation */}
                    <div className="hidden md:flex space-x-4">
                        <Link to="login" className="py-2 px-4 bg-transparent border border-white rounded-lg hover:bg-white hover:text-indigo-600 transition duration-300">
                            Sign In
                        </Link>
                        <Link to="sign-up" className="py-2 px-4 bg-yellow-400 text-indigo-900 rounded-lg font-semibold hover:bg-yellow-300 transition duration-300">
                            Create an Account
                        </Link>
                    </div>
                </nav>
            </header>

            {/* --- Mobile Menu (Visible only when open) --- */}
            {isMenuOpen && (
                <div className="absolute top-0 left-0 w-full h-full bg-indigo-900 text-white p-6 z-50 animate-fade-in-down">
                    <div className="flex justify-end mb-6">
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                    <div className="flex flex-col space-y-6 text-center">
                        <Link to="login" className="py-3 px-8 text-lg font-semibold rounded-lg bg-transparent border-2 border-white hover:bg-white hover:text-indigo-900 transition duration-300">
                            Sign In
                        </Link>
                        <Link to="sign-up" className="py-3 px-8 text-lg font-semibold rounded-lg bg-yellow-400 text-indigo-900 hover:bg-yellow-300 transition duration-300">
                            Create an Account
                        </Link>
                    </div>
                </div>
            )}

            {/* --- Hero Section --- */}
            <section className="bg-gradient-to-br from-indigo-500 to-blue-600 text-white py-16 sm:py-24 lg:py-32">
                <div className="container mx-auto text-center px-4 sm:px-6 lg:px-8">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
                        Turn Customers into Loyal Fans.
                    </h1>
                    <p className="mt-4 text-xl sm:text-2xl max-w-2xl mx-auto">
                        The simplest way for local businesses in South Africa to launch powerful loyalty programs and keep customers coming back.
                    </p>
                    <div className="mt-8">
                        <Link to="sign-up" className="py-3 px-8 text-lg font-semibold rounded-full bg-yellow-400 text-indigo-900 hover:bg-yellow-300 transition duration-300 transform hover:scale-105 shadow-xl">
                            Get Started Now
                        </Link>
                    </div>
                </div>
            </section>

            {/* --- Features Section --- */}
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h2 className="text-3xl font-extrabold text-gray-900">
                            Features Built for Your Business
                        </h2>
                        <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                            LocalRewards gives you the tools you need to build strong customer relationships.
                        </p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
                        <div className="bg-white rounded-xl shadow-lg px-6 py-10 text-center transform transition-transform duration-300 hover:scale-105">
                            <div className="w-16 h-16 my-8 mx-auto flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                <SparklesIcon className="h-8 w-8" />
                            </div>
                            <h3 className="mt-4 text-xl font-bold text-gray-900">Digital Loyalty Programs</h3>
                            <p className="mt-2 text-gray-600">
                                Easily create and manage a points-based loyalty program without cards or stamps.
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg px-6 py-10 text-center transform transition-transform duration-300 hover:scale-105">
                            <div className="w-16 h-16 my-8 mx-auto flex items-center justify-center rounded-full bg-green-100 text-green-600">
                                <ChartBarIcon className="h-8 w-8" />
                            </div>
                            <h3 className="mt-4 text-xl font-bold text-gray-900">Powerful Analytics</h3>
                            <p className="mt-2 text-gray-600">
                                Track customer visits, see what's popular, and measure the success of your loyalty program.
                            </p>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg px-6 py-10 text-center transform transition-transform duration-300 hover:scale-105">
                            <div className="w-16 h-16 my-8 mx-auto flex items-center justify-center rounded-full bg-purple-100 text-purple-600">
                                <UsersIcon className="h-8 w-8" />
                            </div>
                            <h3 className="mt-4 text-xl font-bold text-gray-900">Effortless Customer Management</h3>
                            <p className="mt-2 text-gray-600">
                                View customer profiles, reward history, and engage directly from your dashboard.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- Testimonials Section --- */}
            <section className="bg-gray-800 text-white py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <blockquote className="max-w-3xl mx-auto italic text-xl sm:text-2xl leading-relaxed">
                        "LocalRewards transformed our customer engagement. Our repeat business has increased by 30% since
                        we started using it. The R499.00 a month is an absolute no-brainer."
                    </blockquote>
                    <p className="mt-4 text-lg font-semibold text-yellow-400">- Samantha P., Cafe Owner in Sandton</p>
                </div>
            </section>

            {/* --- Pricing Section --- */}
            <section className="py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-extrabold text-gray-900">
                        Simple & Affordable Pricing
                    </h2>
                    <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                        All the features you need to grow your business, with one transparent monthly price.
                    </p>
                    <div className="mt-10 max-w-sm mx-auto bg-white rounded-xl shadow-lg p-8 border-4 border-indigo-600">
                        <h3 className="text-3xl font-bold text-gray-900">Pro Plan</h3>
                        <div className="flex items-center justify-center my-4">
                            {/* Updated to display South African Rand sign */}
                            <span className="text-6xl font-extrabold text-gray-900">R</span>
                            <span className="text-6xl font-extrabold text-gray-900">499</span>
                            <span className="text-2xl font-medium text-gray-500 mt-3">/mo</span>
                        </div>
                        <p className="text-gray-500">All-in-one loyalty platform</p>
                        <ul className="mt-6 space-y-3 text-left text-gray-600">
                            <li className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> Unlimited customers
                            </li>
                            <li className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> Digital loyalty cards
                            </li>
                            <li className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> Real-time analytics
                            </li>
                            <li className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" /> Dedicated support
                            </li>
                        </ul>
                        <div className="mt-8">
                            <Link to="sign-up" className="mt-8 w-full py-3 px-8 text-lg font-semibold rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition duration-300 transform hover:scale-105">
                                Start Your 30-Day Free Trial
                            </Link>
                        </div>
                        <p className="mt-4 text-sm text-gray-500">No credit card required for trial.</p>
                    </div>
                </div>
            </section>

            {/* --- Final CTA Section --- */}
            <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-extrabold">
                        Ready to grow your business?
                    </h2>
                    <p className="mt-4 text-lg max-w-2xl mx-auto">
                        Sign up today and see how LocalRewards can help you build lasting customer relationships.
                    </p>
                    <div className="mt-8">
                        <Link to="sign-up" className="mt-8 py-3 px-8 text-lg font-semibold rounded-full bg-yellow-400 text-indigo-900 hover:bg-yellow-300 transition duration-300 transform hover:scale-105 shadow-xl">
                            Sign Up for Free Trial
                        </Link>
                    </div>
                </div>
            </section>

            {/* --- Footer --- */}
            <footer className="bg-gray-900 text-gray-400 py-8">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p>&copy; 2025 LocalRewards. All rights reserved.</p>
                </div>
            </footer>
        </div>
    )
}

export default LandingPage